import React, { useState, useEffect } from 'react';
import { Route, Switch, Redirect, useLocation } from "react-router-dom";
import { Routes } from "../routes";
import { useAuth } from "../contexts/AuthContext";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';

// pages
import Presentation from "./Presentation";
import Upgrade from "./Upgrade";
import DashboardOverview from "./dashboard/DashboardOverview";
import Transactions from "./Transactions";
import Settings from "./Settings";
import BootstrapTables from "./tables/BootstrapTables";
import Signin from "./examples/Signin";
import Signup from "./examples/Signup";
import ForgotPassword from "./examples/ForgotPassword";
import ResetPassword from "./examples/ResetPassword";
import Lock from "./examples/Lock";
import NotFoundPage from "./examples/NotFound";
import ServerError from "./examples/ServerError";
import AccountsList from "./accounts/AccountsList";
import AccountsAdd from "./accounts/AccountsAdd";
import AccountsEdit from "./accounts/AccountsEdit";
import VoiceVideoRecordings from "./accounts/VoiceVideoRecordings";
import LikeList from "./accounts/LikeList";
import CityClusters from "./accounts/CityClusters";
import AdminMessages from "./messages/AdminMessages";
import BulkMessageSend from "./messages/BulkMessageSend";
import QuickUnreplied from "./messages/QuickUnreplied";
import UnrepliedMessages from "./messages/UnrepliedMessages";
import AllMessages from "./messages/AllMessages";
import FavoriteMessages from "./messages/FavoriteMessages";
import MessageReplyScreen from "./messages/MessageReplyScreen";
import QuickMessagesManage from "./messages/QuickMessagesManage";
import AutoMsgSettings from "./autoMsg/AutoMsgSettings";
import AutoMsgPool from "./autoMsg/AutoMsgPool";
import MessageTemplates from "./autoMsg/MessageTemplates";
import AutoMsgCampaigns from "./autoMsg/AutoMsgCampaigns";
import AutoCallMenu from "./autoCall/AutoCallMenu";
import PostsList from "./posts/PostsList";
import CreditPackagePayments from "./payments/CreditPackagePayments";
import VipPackagePayments from "./payments/VipPackagePayments";
import BillingErrors from "./payments/BillingErrors";
import CreditPackages from "./packages/CreditPackages";
import CreditPackagesList from "./packages/CreditPackagesList";
import CreditPackagesAdd from "./packages/CreditPackagesAdd";
import VipPackages from "./packages/VipPackages";
import VipSalesStatistics from "./packages/VipSalesStatistics";
import VipPackagesList from "./packages/VipPackagesList";
import VipPackagesAdd from "./packages/VipPackagesAdd";
import MembersList from "./members/MembersList";
import MembersAdd from "./members/MembersAdd";
import MembersEdit from "./members/MembersEdit";

import StoriesList from "./stories/StoriesList";
import StoriesAdd from "./stories/StoriesAdd";
import ReelsList from "./reels/ReelsList";
import ReelsAdd from "./reels/ReelsAdd";
import CommentsList from "./posts/CommentsList";

import Notifications from "./notifications/Notifications";
import PhotoNotification from "./notifications/PhotoNotification";
import Evaluations from "./evaluations/Evaluations";
import AppSettings from "./settings/AppSettings";
import AiTraining from "./ai/AiTraining";
import Archive from "./archive/Archive";
import ContactRequests from "./contact-requests/ContactRequests";

// documentation pages
import DocsOverview from "./documentation/DocsOverview";
import DocsDownload from "./documentation/DocsDownload";
import DocsQuickStart from "./documentation/DocsQuickStart";
import DocsLicense from "./documentation/DocsLicense";
import DocsFolderStructure from "./documentation/DocsFolderStructure";
import DocsBuild from "./documentation/DocsBuild";
import DocsChangelog from "./documentation/DocsChangelog";

// components
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Preloader from "../components/Preloader";

import Accordion from "./components/Accordion";
import Alerts from "./components/Alerts";
import Badges from "./components/Badges";
import Breadcrumbs from "./components/Breadcrumbs";
import Buttons from "./components/Buttons";
import Forms from "./components/Forms";
import Modals from "./components/Modals";
import Navs from "./components/Navs";
import Navbars from "./components/Navbars";
import Pagination from "./components/Pagination";
import Popovers from "./components/Popovers";
import Progress from "./components/Progress";
import Tables from "./components/Tables";
import Tabs from "./components/Tabs";
import Tooltips from "./components/Tooltips";
import Toasts from "./components/Toasts";

const RouteWithLoader = ({ component: Component, ...rest }) => {
  return (
    <Route {...rest} render={props => {
      let componentToRender;
      try {
        componentToRender = <Component {...props} />;
      } catch (error) {
        console.error("Error rendering component:", error);
        componentToRender = (
          <div className="py-4">
            <div className="alert alert-danger">
              <h5>Sayfa yüklenirken hata oluştu</h5>
              <p>{error.message}</p>
              <button className="btn btn-primary" onClick={() => window.location.reload()}>
                Sayfayı Yenile
              </button>
            </div>
          </div>
        );
      }
      return componentToRender;
    }} />
  );
};

const RouteWithSidebar = ({ component: Component, ...rest }) => {
  const { currentUser, isAdmin } = useAuth();
  const location = useLocation();

  const localStorageIsSettingsVisible = () => {
    return localStorage.getItem('settingsVisible') === 'false' ? false : true
  }

  const [showSettings, setShowSettings] = useState(localStorageIsSettingsVisible);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  const toggleSettings = () => {
    setShowSettings(!showSettings);
    localStorage.setItem('settingsVisible', !showSettings);
  }

  return (
    <Route {...rest} render={props => {
      // Eğer kullanıcı giriş yapmamışsa veya admin değilse Signin sayfasına yönlendir
      if (!currentUser || !isAdmin) {
        return <Redirect to={Routes.Signin.path} />;
      }

      // Component render'ında hata yakalama
      let componentToRender;
      try {
        componentToRender = <Component {...props} />;
      } catch (error) {
        console.error("Error rendering component:", error);
        componentToRender = (
          <div className="py-4">
            <div className="alert alert-danger">
              <h5>Sayfa yüklenirken hata oluştu</h5>
              <p>{error.message}</p>
              <button className="btn btn-primary" onClick={() => window.location.reload()}>
                Sayfayı Yenile
              </button>
            </div>
          </div>
        );
      }

      return (
        <>
          <Sidebar isCollapsed={isSidebarCollapsed} />

          <main className="content" style={{ marginLeft: isSidebarCollapsed ? '0' : '260px', transition: 'margin-left 0.3s ease' }}>
            <div 
              onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
              style={{
                position: 'fixed',
                left: isSidebarCollapsed ? '0' : '260px',
                top: '50%',
                transform: 'translateY(-50%)',
                zIndex: 1040,
                cursor: 'pointer',
                backgroundColor: '#262b40',
                color: 'white',
                padding: '15px 5px',
                borderTopRightRadius: '8px',
                borderBottomRightRadius: '8px',
                transition: 'left 0.3s ease',
                boxShadow: '2px 0 5px rgba(0,0,0,0.2)'
              }}
              title={isSidebarCollapsed ? "Menüyü Aç" : "Menüyü Gizle"}
            >
              <FontAwesomeIcon icon={isSidebarCollapsed ? faChevronRight : faChevronLeft} />
            </div>
            <Navbar />
            <div>
              {componentToRender}
            </div>
            <Footer toggleSettings={toggleSettings} showSettings={showSettings} />
          </main>
        </>
      );
    }}
    />
  );
};

export default () => {
  const { currentUser, isAdmin } = useAuth();

  return (
    <Switch>
      {/* Auth Pages - Public */}
      <RouteWithLoader exact path={Routes.Presentation.path} component={Presentation} />
      <RouteWithLoader exact path={Routes.Signin.path} component={Signin} />
      <RouteWithLoader exact path={Routes.Signup.path} component={Signup} />
      <RouteWithLoader exact path={Routes.ForgotPassword.path} component={ForgotPassword} />
      <RouteWithLoader exact path={Routes.ResetPassword.path} component={ResetPassword} />
      <RouteWithLoader exact path={Routes.Lock.path} component={Lock} />
      <RouteWithLoader exact path={Routes.NotFound.path} component={NotFoundPage} />
      <RouteWithLoader exact path={Routes.ServerError.path} component={ServerError} />

      {/* Main Dashboard - Protected (Admin Only) */}
      <RouteWithSidebar exact path={Routes.DashboardOverview.path} component={DashboardOverview} />

      {/* pages */}
      <RouteWithSidebar exact path={Routes.Upgrade.path} component={Upgrade} />
      <RouteWithSidebar exact path={Routes.Transactions.path} component={Transactions} />
      <RouteWithSidebar exact path={Routes.Settings.path} component={Settings} />
      <RouteWithSidebar exact path={Routes.BootstrapTables.path} component={BootstrapTables} />

      {/* accounts */}
      <RouteWithSidebar exact path={Routes.AccountsList.path} component={AccountsList} />
      <RouteWithSidebar exact path={Routes.AccountsAdd.path} component={AccountsAdd} />
      <RouteWithSidebar exact path={Routes.AccountsEdit.path} component={AccountsEdit} />
      <RouteWithSidebar exact path={Routes.VoiceVideoRecordings.path} component={VoiceVideoRecordings} />
      <RouteWithSidebar exact path={Routes.LikeList.path} component={LikeList} />
      <RouteWithSidebar exact path={Routes.CityClusters.path} component={CityClusters} />

      {/* messages */}
      <RouteWithSidebar exact path={Routes.AdminMessages.path} component={AdminMessages} />
      <RouteWithSidebar exact path={Routes.BulkMessageSend.path} component={BulkMessageSend} />
      <RouteWithSidebar exact path={Routes.QuickUnreplied.path} component={QuickUnreplied} />
      <RouteWithSidebar exact path={Routes.UnrepliedMessages.path} component={UnrepliedMessages} />
      <RouteWithSidebar exact path={Routes.AllMessages.path} component={AllMessages} />
      <RouteWithSidebar exact path={Routes.FavoriteMessages.path} component={FavoriteMessages} />
      <RouteWithSidebar exact path={Routes.MessageReply.path} component={MessageReplyScreen} />
      <RouteWithSidebar exact path={Routes.QuickMessagesManage.path} component={QuickMessagesManage} />

      <RouteWithSidebar exact path={Routes.AutoMsgSettings.path} component={AutoMsgSettings} />
      <RouteWithSidebar exact path={Routes.AutoMsgPool.path} component={AutoMsgPool} />
      <RouteWithSidebar exact path={Routes.MessageTemplates.path} component={MessageTemplates} />
      <RouteWithSidebar exact path={Routes.AutoMsgCampaigns.path} component={AutoMsgCampaigns} />
      <RouteWithSidebar exact path={Routes.BulkCallMenu.path} component={AutoCallMenu} />

      {/* posts */}
      <RouteWithSidebar exact path={Routes.PostsList.path} component={PostsList} />

      {/* payments */}
      <RouteWithSidebar exact path={Routes.CreditPackagePayments.path} component={CreditPackagePayments} />
      <RouteWithSidebar exact path={Routes.VipPackagePayments.path} component={VipPackagePayments} />
      <RouteWithSidebar exact path={Routes.BillingErrors.path} component={BillingErrors} />

      {/* packages */}
      <RouteWithSidebar exact path={Routes.CreditPackages.path} component={CreditPackages} />
      <RouteWithSidebar exact path={Routes.CreditPackagesList.path} component={CreditPackagesList} />
      <RouteWithSidebar exact path={Routes.CreditPackagesAdd.path} component={CreditPackagesAdd} />
      <RouteWithSidebar exact path={Routes.CreditPackagesEdit.path} component={CreditPackagesAdd} />
      <RouteWithSidebar exact path={Routes.VipPackages.path} component={VipPackages} />
      <RouteWithSidebar exact path={Routes.VipStatistics.path} component={VipSalesStatistics} />
      <RouteWithSidebar exact path={Routes.VipPackagesList.path} component={VipPackagesList} />
      <RouteWithSidebar exact path={Routes.VipPackagesAdd.path} component={VipPackagesAdd} />
      <RouteWithSidebar exact path={Routes.VipPackagesEdit.path} component={VipPackagesAdd} />

      {/* members */}
      <RouteWithSidebar exact path={Routes.MembersList.path} component={MembersList} />
      <RouteWithSidebar exact path={Routes.MembersAdd.path} component={MembersAdd} />
      <RouteWithSidebar exact path={Routes.MembersEdit.path} component={MembersEdit} />



      {/* stories */}
      <RouteWithSidebar exact path={Routes.StoriesList.path} component={StoriesList} />
      <RouteWithSidebar exact path={Routes.StoriesAdd.path} component={StoriesAdd} />

      {/* reels */}
      <RouteWithSidebar exact path={Routes.ReelsList.path} component={ReelsList} />
      <RouteWithSidebar exact path={Routes.ReelsAdd.path} component={ReelsAdd} />

      {/* comments */}
      <RouteWithSidebar exact path={Routes.CommentsList.path} component={CommentsList} />



      {/* notifications */}
      <RouteWithSidebar exact path={Routes.Notifications.path} component={Notifications} />
      <RouteWithSidebar exact path={Routes.PhotoNotification.path} component={PhotoNotification} />

      {/* evaluations */}
      <RouteWithSidebar exact path={Routes.Evaluations.path} component={Evaluations} />

      {/* settings */}
      <RouteWithSidebar exact path={Routes.AppSettings.path} component={AppSettings} />
      <RouteWithSidebar exact path={Routes.AiTraining.path} component={AiTraining} />

      {/* archive */}
      <RouteWithSidebar exact path={Routes.Archive.path} component={Archive} />

      {/* contact requests */}
      <RouteWithSidebar exact path={Routes.ContactRequests.path} component={ContactRequests} />

      {/* components */}
      <RouteWithSidebar exact path={Routes.Accordions.path} component={Accordion} />
      <RouteWithSidebar exact path={Routes.Alerts.path} component={Alerts} />
      <RouteWithSidebar exact path={Routes.Badges.path} component={Badges} />
      <RouteWithSidebar exact path={Routes.Breadcrumbs.path} component={Breadcrumbs} />
      <RouteWithSidebar exact path={Routes.Buttons.path} component={Buttons} />
      <RouteWithSidebar exact path={Routes.Forms.path} component={Forms} />
      <RouteWithSidebar exact path={Routes.Modals.path} component={Modals} />
      <RouteWithSidebar exact path={Routes.Navs.path} component={Navs} />
      <RouteWithSidebar exact path={Routes.Navbars.path} component={Navbars} />
      <RouteWithSidebar exact path={Routes.Pagination.path} component={Pagination} />
      <RouteWithSidebar exact path={Routes.Popovers.path} component={Popovers} />
      <RouteWithSidebar exact path={Routes.Progress.path} component={Progress} />
      <RouteWithSidebar exact path={Routes.Tables.path} component={Tables} />
      <RouteWithSidebar exact path={Routes.Tabs.path} component={Tabs} />
      <RouteWithSidebar exact path={Routes.Tooltips.path} component={Tooltips} />
      <RouteWithSidebar exact path={Routes.Toasts.path} component={Toasts} />

      {/* documentation */}
      <RouteWithSidebar exact path={Routes.DocsOverview.path} component={DocsOverview} />
      <RouteWithSidebar exact path={Routes.DocsDownload.path} component={DocsDownload} />
      <RouteWithSidebar exact path={Routes.DocsQuickStart.path} component={DocsQuickStart} />
      <RouteWithSidebar exact path={Routes.DocsLicense.path} component={DocsLicense} />
      <RouteWithSidebar exact path={Routes.DocsFolderStructure.path} component={DocsFolderStructure} />
      <RouteWithSidebar exact path={Routes.DocsBuild.path} component={DocsBuild} />
      <RouteWithSidebar exact path={Routes.DocsChangelog.path} component={DocsChangelog} />

      {/* Default redirect */}
      {currentUser && isAdmin ? (
        <Redirect to={Routes.DashboardOverview.path} />
      ) : (
        <Redirect to={Routes.Signin.path} />
      )}
    </Switch>
  );
};
